import React, { useState } from 'react';

import logo from '../../assets/img/logo/logo.png';

import { Flex, useDisclosure, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, IconButton, VStack, useToast } from '@chakra-ui/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createPortalSession } from '../../services/paymentService';
import { MdApps, MdOutlineAddToPhotos, MdMenu } from 'react-icons/md';

import Dropdown from 'components/dropdown';
import { BsGear } from 'react-icons/bs';

function Header() {
	const { user, userProfile, isAuthenticated, isAdmin, signOut } = useAuth();
	const navigate = useNavigate();
	const { isOpen: isMobileOpen, onOpen: onMobileOpen, onClose: onMobileClose } = useDisclosure();
	const location = useLocation();
	const toast = useToast();
	const [portalLoading, setPortalLoading] = useState(false);

	const getLinkClass = (path) => {
		return location.pathname === path
			? 'font-bold text-gray-900 dark:text-white transition-colors duration-200'
			: 'text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200';
	};

	const handleSignIn = () => {
		navigate('/signin');
	};

	const handleSignOut = async () => {
		await signOut(navigate);
	};

	const handleManageSubscription = async () => {
		if (!userProfile?.userId) return;
		setPortalLoading(true);
		try {
			const { url } = await createPortalSession(userProfile.userId);
			window.location.href = url;
		} catch (err) {
			toast({
				title: 'Could not open subscription management',
				description: err.message || 'Please try again.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			setPortalLoading(false);
		}
	};

	// Function to get display name with fallback
	const getDisplayName = () => {
		if (userProfile?.fullName) {
			return userProfile.fullName;
		}
		return user?.name || user?.email || 'User';
	};

	return (
		<header className=' shadow-sm border-b border-gray-800/40 dark:border-gray-600/25'>
			<div className='mx-auto max-w-screen-lg px-4 sm:px-6 lg:px-8'>
				<div className='flex items-center justify-between py-4'>
					<Link to='/' className='inline-flex items-center shrink-0' title='ValuePay.ai'>
						<img src={logo} className='w-14' alt='logo' />
						<span className='ml-4 text-xl font-semibold hidden sm:block'>ValuePay.ai</span>
					</Link>

					<div className='flex items-center gap-4'>
						<div className='hidden lg:flex items-center space-x-6'>
							<div className={getLinkClass('/')}>
								<Link to='/'>Home</Link>
							</div>
							<div className={getLinkClass('/about')}>
								<Link to='/about'>About Us</Link>
							</div>
							<div className={getLinkClass('/contact')}>
								<Link to='/contact'>Contact Us</Link>
							</div>
							<div className='text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200' onClick={() => navigate('/pricing')}>
								<span className='cursor-pointer'>Pricing</span>
							</div>
							{isAuthenticated && (
								<div className={getLinkClass('/chat')}>
									<Link to="/chat" title='Start Chat'>Start Chat</Link>
								</div>
							)}
							{isAuthenticated && (
								<div className={getLinkClass('/team')}>
									<Link to="/team" title='Team'>Team</Link>
								</div>
							)}
							{isAdmin ? (
								<div className='rounded-md bg-amber-500 px-2 py-1 text-sm font-bold text-white hover:cursor-pointer hover:bg-amber-600'>
									<Link to='/adminview'>Admin</Link>
								</div>
							) : null}
						</div>

						<Flex className='align-middle space-x-2'>
							{/* Waffle Menu */}
							<Dropdown
								classNames={'py-2 top-10 -left-[173px] w-max'}
								button={
									<button className='h-10 w-10 rounded-lg flex items-center justify-center bg-white hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-800 transition-colors duration-200'>
										<MdApps className='h-6 w-6 text-gray-600 dark:text-gray-300' />
									</button>
								}
								children={
									<div className='relative flex min-w-56 py-4 flex-col justify-start rounded-lg bg-white border border-gray-800/40 dark:border-gray-600/25 dark:!bg-navy-700 dark:text-white'>
										<div className="absolute -top-[7px] right-6 h-3 w-3 rotate-45 border-l border-t border-gray-800/40 bg-white dark:border-gray-600/25 dark:bg-navy-700" />
										<div className='grid grid-cols-2 gap-2'>
											{/* Settings Option */}
											<div className='flex flex-col items-center'>
												<Link to='/settings'>
													<div className='w-16 h-16 rounded-lg flex items-center justify-center mb-2 bg-white hover:bg-blue-200 dark:hover:bg-blue-200/10 dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25'>
														<BsGear />
													</div>
												</Link>
												<div className='text-xs font-medium text-gray-700 dark:text-gray-300 text-center'>
													Settings
												</div>
											</div>

											{/* Placeholder for future menu items */}
											<div className='flex flex-col items-center'>
												<Link to='/more'>
													<div className='w-16 h-16 rounded-lg flex items-center justify-center mb-2 bg-white hover:bg-blue-200 dark:hover:bg-blue-200/10 dark:bg-gray-900 border border-gray-800/40 dark:border-gray-600/25'>
														<MdOutlineAddToPhotos />
													</div>
												</Link>
												<div className='text-xs font-medium text-gray-700 dark:text-gray-300 text-center'>
													More
												</div>
											</div>
										</div>
									</div>
								}
							/>

							{!isAuthenticated ? (
								<button className='rounded-md bg-green-600/65 px-5 py-2.5 text-sm font-medium shadow hover:opacity-50' title='Sign In' onClick={handleSignIn}>
									Sign in
								</button>
							) : (
								<>
									<Dropdown
										button={
											<button className='h-10 w-10 rounded-full border border-solid border-blue-500 hover:border-solid hover:border-amber-500 hover:opacity-80 hover:shadow-inset bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 font-semibold'>
												{getDisplayName().charAt(0).toUpperCase()}
											</button>
										}
										children={
											<div className='relative flex w-72 flex-col justify-start rounded-[20px] bg-white border border-gray-200 dark:border-white/10 dark:!bg-navy-700'>
												<div className="absolute -top-[7px] right-6 h-3 w-3 rotate-45 border-l border-t border-gray-200 bg-white dark:border-white/10 dark:bg-navy-700" />
												<div className='p-4'>
													<div className='flex items-center gap-2'>
														<p className='text-sm font-bold text-navy-700 dark:text-white'>👋 Hey, {getDisplayName()}</p>
													</div>
												</div>
												<div className='h-px w-full bg-gray-200 dark:bg-white/20 ' />

												<div className='flex flex-col p-4'>
													<Flex className=' cursor-pointer pb-2 text-sm text-gray-800 hover:underline dark:text-white hover:dark:text-white'>Profile Settings</Flex>
													<Flex className='mt-3 cursor-pointer pb-2 text-sm text-gray-800 hover:underline dark:text-white hover:dark:text-white'>Newsletter Settings</Flex>
													<Flex
														className={`mt-3 pb-2 text-sm text-gray-800 hover:underline dark:text-white hover:dark:text-white ${portalLoading ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
														onClick={portalLoading ? undefined : handleManageSubscription}
													>
														{portalLoading ? 'Opening...' : 'Manage Subscription'}
													</Flex>
													<button className='brandline mt-2 rounded-md bg-brandLinear px-5 py-2.5 text-sm font-medium shadow hover:opacity-50' onClick={handleSignOut}>
														Sign out
													</button>
												</div>
											</div>
										}
										classNames={'py-2 top-12 -left-[240px] w-max'}
									/>
								</>
							)}
							<IconButton
								display={{ base: 'flex', lg: 'none' }}
								aria-label='Open menu'
								fontSize='24px'
								variant='ghost'
								color='gray.600'
								_dark={{ color: 'gray.300' }}
								icon={<MdMenu />}
								onClick={onMobileOpen}
							/>
						</Flex>
					</div>
				</div>
			</div>

			{/* Mobile Menu Drawer */}
			<Drawer isOpen={isMobileOpen} placement='right' onClose={onMobileClose}>
				<DrawerOverlay />
				<DrawerContent className='dark:!bg-navy-800 dark:text-white'>
					<DrawerCloseButton />
					<DrawerHeader className='border-b border-gray-200 dark:border-white/10'>Menu</DrawerHeader>
					<DrawerBody>
						<VStack spacing={4} align='stretch' className='mt-4'>
							<Link to='/' onClick={onMobileClose} className={getLinkClass('/')}>
								Home
							</Link>
							<Link to='/about' onClick={onMobileClose} className={getLinkClass('/about')}>
								About Us
							</Link>
							<Link to='/contact' onClick={onMobileClose} className={getLinkClass('/contact')}>
								Contact Us
							</Link>
							<div
								className='cursor-pointer text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
								onClick={() => {
									onMobileClose();
									navigate('/pricing');
								}}
							>
								Pricing
							</div>
							{isAuthenticated && (
								<Link to='/chat' onClick={onMobileClose} className={getLinkClass('/chat')}>
									Start Chat
								</Link>
							)}
							{isAuthenticated && (
								<Link to='/team' onClick={onMobileClose} className={getLinkClass('/team')}>
									Team
								</Link>
							)}
							{isAdmin && (
								<Link to='/adminview' onClick={onMobileClose} className='rounded-md bg-amber-500 px-4 py-2 text-center text-sm font-bold text-white'>
									Admin
								</Link>
							)}
						</VStack>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</header>
	);
}

export default Header;
